import React, { useEffect, useRef, useMemo } from "react";
import {
  iconsPGroup,
  deviceMatchesAnySelection,
} from "../../helpers/measurement-secGroup";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import "./GroupSelector.css";

const normalizeGroupKey = (name) => String(name || "").toLowerCase();

const deviceHasVisibleParams = (device) => {
  const hasBasicVisible =
    Array.isArray(device.basic_parameters) &&
    device.basic_parameters.some((p) => p.visible === 1);

  const hasCalcVisible =
    Array.isArray(device.calculated_parameters) &&
    device.calculated_parameters.some(
      (p) => p.device_calculated_parameter?.visible === 1,
    );

  return hasBasicVisible || hasCalcVisible;
};

const GroupSelector = ({
  allGroups,
  primary,
  setPrimary,
  second,
  setSecond,
  openDropdown,
  setOpenDropdown,
  firstSelect = [],
  filterPrimaryGroup = null,
}) => {
  const containerRef = useRef(null);
  const measurementGroups = useSelector(
    (state) => state.data.measurementGroups,
  );
  const devices = useSelector((state) => state.devices.currentDevices);

  const hasSelection = useMemo(
    () => Array.isArray(firstSelect) && firstSelect.length > 0,
    [firstSelect],
  );

  const handlePrimary = (e) => {
    if (!hasSelection) return;
    const groupName = e.currentTarget.getAttribute("data-name");
    setPrimary(groupName);
    setOpenDropdown({ [groupName]: true });
  };

  const handleSecondarySelect = (groupName) => {
    if (!hasSelection) return;
    setSecond(groupName);
    setOpenDropdown({});
  };

  useEffect(() => {
    if (!hasSelection) {
      if (primary) setPrimary("");
      if (second) setSecond("");
      setOpenDropdown({});
    }
  }, [hasSelection, primary, second, setPrimary, setSecond, setOpenDropdown]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setOpenDropdown({});
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [setOpenDropdown]);

  const primaryGroupsToShow = useMemo(() => {
    if (!filterPrimaryGroup) return allGroups.allPrimary || [];
    const allowedPrimaryGroups = Array.isArray(filterPrimaryGroup)
      ? filterPrimaryGroup.map((group) => String(group || "").toLowerCase())
      : [String(filterPrimaryGroup).toLowerCase()];

    return (allGroups.allPrimary || []).filter((g) =>
      allowedPrimaryGroups.includes(String(g).toLowerCase()),
    );
  }, [allGroups.allPrimary, filterPrimaryGroup]);

  return (
    <div className="group-selector" ref={containerRef}>
      <div className="group-selector__content">
        {primaryGroupsToShow?.map((groupName) => {
          const groupNameStr = String(groupName);
          const groupNameLower = groupNameStr.toLowerCase();

          let hasPrimaryDevices = false;

          if (Array.isArray(devices) && hasSelection) {
            hasPrimaryDevices = devices.some((d) => {
              const samePrimary =
                String(d.primary_group || "").toLowerCase() === groupNameLower;
              if (!samePrimary) return false;
              if (!deviceMatchesAnySelection(d, firstSelect)) return false;
              return deviceHasVisibleParams(d);
            });
          }

          if (!hasPrimaryDevices && !hasSelection) {
            if (Array.isArray(measurementGroups)) {
              hasPrimaryDevices = measurementGroups.some(
                (m) =>
                  m.groupM && String(m.groupM).toLowerCase() === groupNameLower,
              );
            }
          }

          const isDisabledPrimary = !hasSelection || !hasPrimaryDevices;
          const isActivePrimary = primary === groupName;
          const key = normalizeGroupKey(groupName);

          const iconClass = isActivePrimary
            ? `group-item__icon group-item__icon--${key}-active`
            : "group-item__icon";

          return (
            <div className="group-item" key={groupName}>
              <div
                className={`group-item__primary ${
                  isActivePrimary ? "active" : ""
                } ${isDisabledPrimary ? "disabled" : ""}`}
                onClick={(e) => {
                  if (!isDisabledPrimary) handlePrimary(e);
                }}
                data-name={groupName}
                title={groupName}
                aria-disabled={isDisabledPrimary}
              >
                <span className={iconClass}>{iconsPGroup[groupName]}</span>
                <span className="group-item__label">
                  <FormattedMessage
                    id={`GrupoPrimario_${groupName}`.replace(/\s+/g, "")}
                    defaultMessage={groupName}
                  />
                </span>
              </div>

              {openDropdown[groupName] && hasSelection && (
                <ul className="group-item__secondary">
                  {allGroups.allSecondary?.map((secondaryGroup) => {
                    const secStr = String(secondaryGroup || "");
                    const secLower = secStr.toLowerCase();

                    let hasSecondaryDevices = false;

                    if (Array.isArray(devices) && hasSelection) {
                      hasSecondaryDevices = devices.some((d) => {
                        const samePrimary =
                          String(d.primary_group || "").toLowerCase() ===
                          groupNameLower;
                        const sameSecondary =
                          String(d.secondary_group || "").toLowerCase() ===
                          secLower;

                        if (!samePrimary || !sameSecondary) return false;
                        if (!deviceMatchesAnySelection(d, firstSelect))
                          return false;

                        return deviceHasVisibleParams(d);
                      });
                    }

                    const isDisabledSecondary = !hasSecondaryDevices;
                    const isActiveSecondary = second === secondaryGroup;

                    return (
                      <li
                        key={secondaryGroup}
                        className={`secondary-item ${
                          isActiveSecondary ? "active" : ""
                        } ${isDisabledSecondary ? "disabled" : ""}`}
                        onClick={() => {
                          if (!isDisabledSecondary)
                            handleSecondarySelect(secondaryGroup);
                        }}
                      >
                        <FormattedMessage
                          id={`GrupoSecundario_${secondaryGroup}`.replace(
                            /\s+/g,
                            "",
                          )}
                          defaultMessage={secondaryGroup}
                        />
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          );
        })}

        <div className="group-selector__selected-secondary">
          {second ? (
            <>
              <span className="group-selector__selected-secondary-label">
                <FormattedMessage
                  id="GrupoSecundarioSeleccionadoLabel"
                  defaultMessage="Grupo secundario seleccionado:"
                />
              </span>
              <span className="group-selector__selected-secondary-value">
                <FormattedMessage
                  id={`GrupoSecundario_${second}`.replace(/\s+/g, "")}
                  defaultMessage={second}
                />
              </span>
            </>
          ) : (
            <span className="group-selector__selected-secondary-label">
              <FormattedMessage
                id="GrupoSecundarioSeleccionadoVacio"
                defaultMessage="Grupo secundario seleccionado: -"
              />
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default GroupSelector;
